import React, { useState } from 'react';
import { Play, AlarmClock, Check, CalendarClock } from 'lucide-react';
import { CARD, LABEL, Btn, Pill, Stat, cx, when, INPUT } from '../contacts/ui';
import { fmtWhen } from './util';

export const StatsStrip = ({ stats }) => (
  <div className="mb-5" data-testid="calling-stats">
    <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
      <Stat label="Calls today" value={stats?.calls_today ?? '–'} hint={stats ? `${stats.answered_today} answered` : ''} testid="calling-stat-calls" />
      <Stat label="Remaining" value={stats?.remaining ?? '–'} hint="in your open queues" testid="calling-stat-remaining" />
      <Stat label="Callbacks due" value={stats?.callbacks_due ?? '–'} hint={stats?.callbacks_overdue ? `${stats.callbacks_overdue} overdue` : 'today'} tone={stats?.callbacks_overdue ? 'text-[var(--amber)]' : undefined} testid="calling-stat-callbacks" />
      <div className={cx(CARD, 'px-4 py-3.5')} data-testid="calling-stat-progress">
        <p className={LABEL}>Daily progress</p>
        <p className="text-2xl font-bold tabular-nums tracking-tight text-[var(--ink)]">{stats ? `${stats.progress}%` : '–'}</p>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--hover)]"><div className="h-full rounded-full bg-[var(--green)] transition-[width] duration-700" style={{ width: `${stats?.progress || 0}%` }} /></div>
      </div>
    </div>
  </div>
);

const CallbackRow = ({ c, onDone, onMove }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState('');
  return (
    <li className="rounded-xl border border-[var(--line)] bg-[var(--surface-2)] px-3 py-2.5" data-testid={`callback-${c.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[var(--ink)]">{c.name}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--muted)]">
            <Pill className={c.overdue ? 'bg-[var(--amber-bg)] text-[var(--amber-ink)]' : 'bg-[var(--hover)] text-[var(--ink-2)]'}>{c.overdue ? 'Overdue' : new Date(c.due_at) <= new Date(Date.now() + 14 * 3600e3) ? 'Due' : 'Upcoming'}</Pill>{when(c.due_at)}
          </p>
          {c.note && <p className="mt-1 line-clamp-2 text-xs text-[var(--ink-2)]">{c.note}</p>}
        </div>
        <button type="button" onClick={() => setEdit((x) => !x)} title="Reschedule" data-testid={`callback-move-${c.id}`} className="rounded-full p-1.5 text-[var(--muted)] hover:bg-[var(--hover)]"><CalendarClock className="h-4 w-4" /></button>
        <button type="button" onClick={() => onDone(c)} title="Mark done" data-testid={`callback-done-${c.id}`} className="rounded-full p-1.5 text-[var(--green)] hover:bg-[var(--hover)]"><Check className="h-4 w-4" /></button>
      </div>
      {edit && <div className="mt-2 flex gap-1.5"><input type="datetime-local" value={v} onChange={(e) => setV(e.target.value)} className={cx(INPUT, 'py-1.5 text-xs')} data-testid={`callback-move-input-${c.id}`} />
        <Btn tone="solid" size="sm" disabled={!v} onClick={() => { onMove(c, new Date(v).toISOString()); setEdit(false); }} data-testid={`callback-move-save-${c.id}`}>Save</Btn></div>}
    </li>
  );
};

export const CallbacksPanel = ({ items, due, onStart, onDone, onMove }) => (
  <section className={cx(CARD, 'p-4')} data-testid="calling-callbacks">
    <div className="mb-3 flex items-center justify-between gap-2">
      <p className={cx(LABEL, 'mb-0 flex items-center gap-1.5')}><AlarmClock className="h-3.5 w-3.5" />Callbacks</p>
      {due > 0 && <Btn tone="solid" size="sm" onClick={onStart} data-testid="calling-callbacks-start"><Play className="h-3.5 w-3.5" />Call {due} due</Btn>}
    </div>
    {!items.length ? <p className="text-sm text-[var(--muted)]" data-testid="calling-callbacks-empty">No callbacks booked. Choose "Call back later" on a call to book one.</p> : (
      <ul className="max-h-[340px] space-y-1.5 overflow-y-auto">{items.map((c) => <CallbackRow key={c.id} c={c} onDone={onDone} onMove={onMove} />)}</ul>
    )}
  </section>
);

export const ResumeList = ({ sessions, onResume }) => {
  const [all, setAll] = useState(false);
  if (!sessions.length) return null;
  return (
    <section className={cx(CARD, 'p-4')} data-testid="calling-resume-list">
      <p className={LABEL}>Pick up where you left off</p>
      <ul className="space-y-1.5">
        {(all ? sessions : sessions.slice(0, 3)).map((s) => (
          <li key={s.id} className="flex items-center gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2.5" data-testid={`calling-resume-${s.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-[var(--ink)]">{s.label}</p>
              <p className="text-xs text-[var(--muted)]">{s.done}/{s.total} called · {fmtWhen(s.updated_at)}</p>
            </div>
            <Btn tone="ghost" size="sm" onClick={() => onResume(s.id)} data-testid={`calling-resume-btn-${s.id}`}><Play className="h-3.5 w-3.5" />Resume</Btn>
          </li>))}
      </ul>
      {sessions.length > 3 && <button type="button" onClick={() => setAll((v) => !v)} data-testid="calling-resume-toggle" className="mt-2 text-xs font-semibold text-[var(--accent-ink)] hover:underline">{all ? 'Show fewer' : `Show all ${sessions.length}`}</button>}
    </section>
  );
};
